/**
 * Hook: Receive ecash — decode token, swap at mint, store fresh proofs.
 */

import { useState, useCallback, useRef } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { generateSecret, blindMessage, serializeBlindedMessage, constructUnblindedSignature, pointFromHex } from '@/lib/cashu/blind-signature';
import { splitAmount } from '@/lib/cashu/split';
import { decodeToken, generateRequestId } from '@/lib/cashu/token';
import { createSeal, createGiftWrap, unwrapMessage } from '@/lib/nostr/gift-wrap';
import { getRequestKind, getResponseKind } from '@/lib/nostr/operations';
import type { SwapRequest, SwapResponse } from '@/lib/nostr/operations';
import { addProofs, addTransaction } from '@/lib/storage/wallet-store';
import type { Keyset } from '@/lib/cashu/keyset';
import { parseEcashDMEvent } from '@/lib/nostr/ecash-dm';

export type ReceiveState =
  | { step: 'idle' }
  | { step: 'decoding' }
  | { step: 'swapping' }
  | { step: 'done'; amount: number }
  | { step: 'error'; error: string };

export function useReceiveEcash() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutate: publish } = useNostrPublish();
  const [state, setState] = useState<ReceiveState>({ step: 'idle' });
  const subRef = useRef<any>(null);

  /** Receive ecash from a token string — swap at mint to get fresh proofs */
  const receiveToken = useCallback(async (
    tokenString: string,
    mintKeysets: Keyset[],
    mintPubkey: string,
    userPrivkey: string,
  ) => {
    if (!user) {
      setState({ step: 'error', error: 'Not logged in' });
      return;
    }

    try {
      setState({ step: 'decoding' });

      // Decode the token
      const token = decodeToken(tokenString);

      if (token.mint !== mintPubkey) {
        setState({ step: 'error', error: 'Token mint does not match selected mint' });
        return;
      }

      const totalAmount = token.proofs.reduce((s, p) => s + p.amount, 0);

      // Split amount into denominations matching the mint's keyset
      const activeKeyset = mintKeysets.find(k => k.active);
      if (!activeKeyset) {
        setState({ step: 'error', error: 'Mint has no active keyset' });
        return;
      }

      const maxDenom = Math.max(...Object.keys(activeKeyset.pubkeys).map(Number));
      const denominations = splitAmount(totalAmount, maxDenom);
      for (const d of denominations) {
        if (!(d in activeKeyset.pubkeys)) {
          setState({ step: 'error', error: `Keyset missing denomination ${d}` });
          return;
        }
      }

      // Create blinded messages for new proofs
      const rawMessages = denominations.map((denom) => {
        const raw = blindMessage(new TextEncoder().encode(generateSecret()));
        raw.amount = denom;
        return raw;
      });

      setState({ step: 'swapping' });

      const requestId = generateRequestId();

      // Build swap request: send old proofs, get new blinded signatures
      const swapRequest: SwapRequest = {
        op: 'swap',
        requestId,
        proofs: token.proofs.map(p => ({
          secret: p.secret,
          C: p.C,
          id: p.id,
          amount: p.amount,
        })),
        blindedMessages: rawMessages.map(serializeBlindedMessage),
      };

      const rumor = {
        kind: getRequestKind(),
        content: JSON.stringify(swapRequest),
        tags: [['p', mintPubkey]],
        created_at: Math.floor(Date.now() / 1000),
        pubkey: user.pubkey,
      };

      const seal = createSeal(rumor, userPrivkey, mintPubkey);
      const giftWrap = createGiftWrap(seal, mintPubkey);

      await publish(giftWrap);

      // Subscribe for response
      subRef.current = nostr.sub([{
        kinds: [1059],
        '#p': [user.pubkey],
        limit: 100,
      }]);

      subRef.current.on('event', (event: any) => {
        try {
          const { rumor: respRumor } = unwrapMessage(event, userPrivkey);
          if (respRumor.kind !== getResponseKind()) return;

          const response = JSON.parse(respRumor.content);
          if (response.requestId !== requestId) return;

          subRef.current?.unsub();

          if (response.status === 'error') {
            setState({ step: 'error', error: response.error });
            return;
          }

          const ok = response as SwapResponse;

          // Unblind signatures to get fresh proofs
          const proofs: any[] = [];
          for (let i = 0; i < ok.blindedSignatures.length; i++) {
            const bs = ok.blindedSignatures[i];
            const raw = rawMessages[i];
            if (!raw) continue;

            const pubkeyHex = activeKeyset.pubkeys[raw.amount];
            if (!pubkeyHex) continue;

            const pubKeyPoint = pointFromHex(pubkeyHex);

            const unblinded = constructUnblindedSignature(
              { C_: bs.C_, id: bs.id },
              raw.r,
              raw.secret,
              pubKeyPoint,
              raw.amount,
            );

            proofs.push({
              secret: unblinded.secret,
              C: unblinded.C,
              id: unblinded.id,
              amount: unblinded.amount,
              mintNpub: mintPubkey,
              acquiredAt: Date.now(),
            });
          }

          // Store new proofs
          addProofs(proofs);

          // Record transaction
          addTransaction({
            id: generateRequestId(),
            type: 'receive',
            mintNpub: mintPubkey,
            amount: totalAmount,
            timestamp: Date.now(),
            status: 'confirmed',
            tokenString,
          });

          setState({ step: 'done', amount: totalAmount });
        } catch {
          // ignore
        }
      });
    } catch (err) {
      setState({ step: 'error', error: (err as Error).message });
    }
  }, [user, nostr, publish]);

  /** Receive ecash from a Nostr DM */
  const receiveFromDM = useCallback(async (
    giftWrapEvent: any,
    userPrivkey: string,
    mintKeysets: Keyset[],
  ) => {
    const parsed = parseEcashDMEvent(giftWrapEvent, userPrivkey);
    if (!parsed) {
      setState({ step: 'error', error: 'Invalid ecash DM' });
      return;
    }
    return receiveToken(parsed.token, mintKeysets, parsed.mintPubkey, userPrivkey);
  }, [receiveToken]);

  const reset = useCallback(() => {
    subRef.current?.unsub();
    setState({ step: 'idle' });
  }, []);

  return { state, receiveToken, receiveFromDM, reset };
}
